import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Textarea from "../../components/common/Textarea";
import {
  useCashRefundPosOrderMutation,
  useLazyGetPosOrderPaymentsQuery,
  useLazyGetPosOrderQuery,
  useLazyGetPosOrdersQuery,
  useRefundPosOrderMutation,
} from "../../store/api";
import {
  createCashRefundIdempotencyKey,
  formatCashRefundMoney,
  getCashRefundError,
  getRecordedRefundReason,
  getRefundPaymentState,
} from "../../utils/cashRefund";
import { formatApiDateTime } from "../../utils/dateTime";

const ORDER_SEARCH_FROM = "2000-01-01T00:00:00.000Z";

const toLocalDateInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const todayLocalDateInputValue = () =>
  toLocalDateInputValue(new Date().toISOString());

const fromLocalDateInputValue = (value, { endOfDay = false } = {}) => {
  if (!value) return undefined;
  const trimmedValue = String(value).trim();
  const isoDateParts = trimmedValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const displayDateParts = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);

  const [, first = "", second = "", third = ""] =
    isoDateParts || displayDateParts || [];
  const year = Number(
    isoDateParts ? first : third.length === 2 ? `20${third}` : third,
  );
  const month = Number(isoDateParts ? second : second);
  const day = Number(isoDateParts ? third : first);

  if (!year || !month || !day) return undefined;
  const parsed = new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const CashRefund = () => {
  const navigate = useNavigate();
  const { orderId: routeOrderId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderInput, setOrderInput] = useState(
    searchParams.get("orderNo") || "",
  );
  const [businessDateInput, setBusinessDateInput] = useState(
    toLocalDateInputValue(searchParams.get("from")) ||
      toLocalDateInputValue(searchParams.get("to")) ||
      todayLocalDateInputValue(),
  );
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMode, setSearchMode] = useState("idle");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [payments, setPayments] = useState([]);
  const [reasonCode, setReasonCode] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [justRefundedOrderId, setJustRefundedOrderId] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [refundPaymentMethod, setRefundPaymentMethod] = useState("CASH");
  const businessDateInputRef = useRef(null);

  const [findOrders, { isFetching: isSearching }] = useLazyGetPosOrdersQuery();
  const [loadOrder, { isFetching: isLoadingOrder }] = useLazyGetPosOrderQuery();
  const [loadPayments, { isFetching: isLoadingPayments }] =
    useLazyGetPosOrderPaymentsQuery();
  const [cashRefund, { isLoading: isCashRefunding }] =
    useCashRefundPosOrderMutation();
  const [refundPosOrder, { isLoading: isCardRefunding }] =
    useRefundPosOrderMutation();
  const isDetailRoute = Boolean(routeOrderId);

  const orderNo = selectedOrder?.orderNo || "";
  const masterToken =
    selectedOrder?.masterTokenNo || selectedOrder?.masterToken || "";
  const grandTotal = Number(
    selectedOrder?.totals?.grandTotal ?? selectedOrder?.grandTotal ?? 0,
  );
  const paymentState = useMemo(
    () => getRefundPaymentState(payments, grandTotal, refundPaymentMethod),
    [payments, grandTotal, refundPaymentMethod],
  );
  const finalReason = String(reasonCode || "").trim();
  const isLoadingDetails = isLoadingOrder || isLoadingPayments;
  const isRefunding = isCashRefunding || isCardRefunding;
  const canOpenConfirmation =
    Boolean(selectedOrder) && paymentState.isEligible && !isRefunding;
  const lines = Array.isArray(selectedOrder?.lines) ? selectedOrder.lines : [];

  const resetSelection = useCallback(() => {
    setSelectedOrder(null);
    setPayments([]);
    setReasonCode("");
    setFieldError("");
    setMessage("");
    setErrorMessage("");
    setIsConfirmOpen(false);
    setIsSuccessOpen(false);
    setJustRefundedOrderId("");
    setIdempotencyKey("");
    setRefundPaymentMethod("CASH");
  }, []);

  const getOrderSummaryTotal = (order) =>
    Number(order?.totals?.grandTotal ?? order?.grandTotal ?? order?.total ?? 0);

  const openOrderDetails = useCallback(
    async (order) => {
      if (!order?.orderId) return;
      setErrorMessage("");
      setMessage("");
      setSelectedOrder(null);
      setPayments([]);
      setReasonCode("");

      try {
        const [orderDetail, paymentRows] = await Promise.all([
          loadOrder(order.orderId).unwrap(),
          loadPayments(order.orderId).unwrap(),
        ]);
        const normalizedPayments = Array.isArray(paymentRows)
          ? paymentRows
          : [];
        setSelectedOrder({ ...order, ...orderDetail });
        setPayments(normalizedPayments);
        setReasonCode(getRecordedRefundReason(normalizedPayments));
        return true;
      } catch (error) {
        setErrorMessage(
          getCashRefundError(error, "Unable to load this order."),
        );
        return false;
      }
    },
    [loadOrder, loadPayments],
  );

  const executeSearch = useCallback(
    async ({
      searchType = "detail",
      orderNo = orderInput,
      from = businessDateInput,
      to = businessDateInput,
      updateOrderInput = true,
    } = {}) => {
      const requestedOrderNo = String(orderNo ?? "").trim();
      if (!requestedOrderNo) {
        setFieldError("Order number is required.");
        return;
      }

      const displayFrom = fromLocalDateInputValue(from);
      const displayTo = fromLocalDateInputValue(to);
      const resolvedFrom = fromLocalDateInputValue(from) || ORDER_SEARCH_FROM;
      const resolvedTo =
        fromLocalDateInputValue(to, { endOfDay: true }) ||
        new Date().toISOString();

      resetSelection();
      setSearchResults([]);
      setHasSearched(true);
      setSearchMode(searchType === "detail" ? "detail" : "list");
      if (updateOrderInput) {
        setOrderInput(requestedOrderNo);
      }
      setSearchParams(
        {
          ...(requestedOrderNo ? { orderNo: requestedOrderNo } : {}),
          ...(displayFrom ? { from: displayFrom } : {}),
          ...(displayTo ? { to: displayTo } : {}),
        },
        { replace: true },
      );

      try {
        const result = await findOrders({
          search:
            searchType === "detail" ? requestedOrderNo || undefined : undefined,
          from: resolvedFrom,
          to: resolvedTo,
          page: 1,
          pageSize: 25,
        }).unwrap();
        const orders = Array.isArray(result?.orders)
          ? result.orders
          : Array.isArray(result)
            ? result
            : [];
        if (searchType === "detail") {
          const normalizedRequested = requestedOrderNo.toLowerCase();
          const exactOrder =
            orders.find((order) =>
              [
                order?.orderNo,
                order?.masterTokenNo,
                order?.masterToken,
                order?.orderId,
              ].some(
                (value) =>
                  String(value || "")
                    .trim()
                    .toLowerCase() === normalizedRequested,
              ),
            ) || (orders.length === 1 ? orders[0] : null);

          if (!exactOrder?.orderId) {
            setErrorMessage(
              "Order not found. Check the order number.",
            );
            setSearchMode("idle");
            return;
          }

          const didLoadOrder = await openOrderDetails(exactOrder);
          if (!didLoadOrder) {
            setSearchMode("idle");
          }
          return;
        }

        setSearchResults(orders);
        if (orders.length > 0) {
          setMessage(
            `${orders.length} order${orders.length === 1 ? "" : "s"} found. Select View to inspect the details.`,
          );
        } else {
          setMessage("No orders matched the selected criteria.");
        }
      } catch (error) {
        setErrorMessage(
          getCashRefundError(error, "Unable to load this order."),
        );
        if (searchType === "detail") {
          setSearchMode("idle");
        }
      }
    },
    [
      findOrders,
      businessDateInput,
      orderInput,
      resetSelection,
      setSearchParams,
      openOrderDetails,
    ],
  );

  useEffect(() => {
    if (routeOrderId) {
      void openOrderDetails({ orderId: routeOrderId });
    }
  }, [openOrderDetails, routeOrderId]);

  useEffect(() => {
    const requestedOrderNo = String(searchParams.get("orderNo") || "").trim();
    const requestedFrom = searchParams.get("from");
    const requestedTo = searchParams.get("to");
    setBusinessDateInput(
      toLocalDateInputValue(requestedFrom) ||
        toLocalDateInputValue(requestedTo) ||
        todayLocalDateInputValue(),
    );
    setOrderInput((current) => {
      if (requestedOrderNo === null) return current;
      return requestedOrderNo;
    });
  }, [searchParams]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const requestedOrderNo = String(orderInput || "").trim();
    if (!requestedOrderNo) {
      setFieldError("Order number is required.");
      return;
    }
    void executeSearch({
      searchType: "detail",
      orderNo: requestedOrderNo,
      from: businessDateInput,
      to: businessDateInput,
    });
  };

  const handleOpenBusinessDatePicker = () => {
    businessDateInputRef.current?.showPicker?.();
  };

  const handleResetSearch = () => {
    setOrderInput("");
    setBusinessDateInput(todayLocalDateInputValue());
    setSearchResults([]);
    setHasSearched(false);
    setSearchMode("idle");
    setSearchParams({}, { replace: true });
    resetSelection();
  };

  const handleBackFromDetails = () => {
    if (isDetailRoute) {
      navigate(
        `/cash-management/refunds${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
      );
      return;
    }

    setSelectedOrder(null);
    setPayments([]);
    setSearchMode("idle");
    setMessage("");
    setErrorMessage("");
    setJustRefundedOrderId("");
    setIdempotencyKey("");
    setOrderInput("");
    setSearchResults([]);
    setSearchParams(
      {
        ...(businessDateInput ? { from: businessDateInput } : {}),
        ...(businessDateInput ? { to: businessDateInput } : {}),
      },
      { replace: true },
    );
  };

  const handleOpenConfirmation = () => {
    setFieldError("");
    setErrorMessage("");
    if (!String(reasonCode || "").trim()) {
      setFieldError("Refund reason is required.");
      return;
    }
    if (!canOpenConfirmation) return;
    setIdempotencyKey(createCashRefundIdempotencyKey());
    setIsConfirmOpen(true);
  };

  const handleCloseConfirmation = () => {
    if (isRefunding) return;
    setIsConfirmOpen(false);
    setIdempotencyKey("");
    setErrorMessage("");
  };

  const handleConfirmRefund = async () => {
    if (
      !selectedOrder?.orderId ||
      !orderNo ||
      !canOpenConfirmation ||
      isRefunding
    )
      return;
    setErrorMessage("");
    setMessage("");

    try {
      const payload = {
        refundAmount: paymentState.refundableAmount,
        reason: finalReason,
        idempotencyKey: idempotencyKey || createCashRefundIdempotencyKey(),
      };
      const refundRequest =
        refundPaymentMethod === "CARD"
          ? refundPosOrder({ orderId: selectedOrder.orderId, payload })
          : cashRefund({ orderId: selectedOrder.orderId, orderNo, payload });
      const updatedFromRefund = await refundRequest.unwrap();

      setSelectedOrder((current) => ({ ...current, ...updatedFromRefund }));
      setPayments((current) => [
        ...current,
        {
          paymentId: `local-${idempotencyKey || Date.now()}`,
          paymentMethod: refundPaymentMethod,
          paymentStatus: "REFUNDED",
          amount: paymentState.refundableAmount,
          currencyCode: paymentState.currencyCode,
          createdAt: new Date().toISOString(),
          changeReason: finalReason,
        },
      ]);
      setIsConfirmOpen(false);
      setIsSuccessOpen(true);
      setJustRefundedOrderId(String(selectedOrder.orderId));
      setIdempotencyKey("");
      setReasonCode(finalReason);
      setMessage("Refund processed successfully.");

      try {
        const [updatedOrder, updatedPayments] = await Promise.all([
          loadOrder(selectedOrder.orderId).unwrap(),
          loadPayments(selectedOrder.orderId).unwrap(),
        ]);
        const normalizedPayments = Array.isArray(updatedPayments)
          ? updatedPayments
          : [];
        setSelectedOrder((current) => ({ ...current, ...updatedOrder }));
        setPayments(normalizedPayments);
        setReasonCode(
          getRecordedRefundReason(normalizedPayments) || finalReason,
        );
      } catch {
        setErrorMessage(
          "Refund completed, but the latest payment history could not be loaded. Search for the order again to refresh it.",
        );
      }
    } catch (error) {
      setErrorMessage(
        getCashRefundError(
          error,
          `Unable to process the ${refundPaymentMethod.toLowerCase()} refund.`,
        ),
      );
    }
  };

  const isShowingDetailView = isDetailRoute || searchMode === "detail";
  const showInlineDetails = Boolean(selectedOrder) && isShowingDetailView;

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title={isShowingDetailView ? "Refund Details" : "Refund"}
        subtitle={
          isShowingDetailView
            ? "Review the order and payment details before processing a refund."
            : "Find an order by order number for the selected business date."
        }
        actions={
          isShowingDetailView ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleBackFromDetails}
              disabled={isSearching || isLoadingDetails || isRefunding}
            >
              Back
            </Button>
          ) : null
        }
      />

      {errorMessage && !isConfirmOpen ? (
        <Alert tone="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}

      {message && !isConfirmOpen ? (
        <Alert
          tone={justRefundedOrderId ? "success" : "info"}
          onClose={() => setMessage("")}
        >
          {message}
        </Alert>
      ) : null}

      {!isShowingDetailView ? (
        <>
          <Card className="overflow-hidden !p-4">
            <form
              className="border-b border-slate-200 p-4 dark:border-slate-700 md:p-5"
              onSubmit={handleSearchSubmit}
            >
              <div className="flex flex-col gap-3 xl:grid xl:grid-cols-[minmax(280px,1.4fr)_minmax(0,240px)_auto_auto] xl:items-end">
                <div className="min-w-0">
                  <Input
                    label="Order Number"
                    type="search"
                    value={orderInput}
                    onChange={(event) => {
                      setOrderInput(event.target.value);
                      setFieldError("");
                    }}
                    error={!selectedOrder ? fieldError : ""}
                    placeholder="Enter order number"
                    autoComplete="off"
                    required
                    disabled={isSearching || isLoadingDetails || isRefunding}
                  />
                </div>
                <Input
                  ref={businessDateInputRef}
                  label="Business Date"
                  type="date"
                  value={businessDateInput}
                  onChange={(event) => {
                    setBusinessDateInput(event.target.value);
                  }}
                  onClick={handleOpenBusinessDatePicker}
                  autoComplete="off"
                  required
                  disabled={isSearching || isLoadingDetails || isRefunding}
                />
                <Button
                  type="submit"
                  size="md"
                  className="w-full shrink-0 xl:min-w-24"
                  disabled={isSearching || isLoadingDetails || isRefunding}
                >
                  {isSearching ? "Searching..." : "Search"}
                </Button>
                <div className="flex xl:self-end">
                  <Button
                    type="button"
                    size="md"
                    variant="secondary"
                    onClick={handleResetSearch}
                    className="w-full shrink-0 xl:w-auto xl:min-w-24"
                    disabled={isSearching || isLoadingDetails || isRefunding}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </form>

            {!hasSearched &&
            !selectedOrder &&
            !isSearching &&
            !isLoadingDetails &&
            !errorMessage ? (
              <div className="p-10 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.8}
                      d="M9 14l2 2 4-4m5-5H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM8 7V5a4 4 0 118 0v2"
                    />
                  </svg>
                </div>
                <h2 className="mt-3 text-base font-semibold text-slate-900 dark:text-slate-100">
                  Search for an order to begin
                </h2>
                <p className="mx-auto mt-1 max-w-lg text-sm text-slate-500 dark:text-slate-400">
                  Order and payment details will appear here after a search.
                </p>
              </div>
            ) : null}

            {searchMode === "list" ? (
              <Card className="overflow-hidden p-0">
                <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Orders
                      </h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {isSearching
                          ? "Searching orders..."
                          : `${searchResults.length} order${searchResults.length === 1 ? "" : "s"} ready for review.`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-[900px]">
                    <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_130px_130px_110px] gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                      <span>Order</span>
                      <span>Master Token</span>
                      <span>Status</span>
                      <span className="text-right">Amount</span>
                      <span className="text-right">Action</span>
                    </div>

                    {searchResults.length > 0 ? (
                      searchResults.map((order) => {
                        const summaryTotal = getOrderSummaryTotal(order);
                        const orderLabel = order?.orderNo || "-";
                        const masterToken =
                          order?.masterTokenNo || order?.masterToken || "-";
                        const statusLabel =
                          order?.displayStatus || order?.status || "Unknown";

                        return (
                          <div
                            key={
                              order?.orderId || `${orderLabel}-${masterToken}`
                            }
                            className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_130px_130px_110px] gap-3 border-b border-slate-100 px-5 py-4 text-sm last:border-b-0 dark:border-slate-800"
                          >
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                                Order {orderLabel}
                              </div>
                              <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                {order?.businessDate ||
                                  order?.createdAt ||
                                  formatApiDateTime(
                                    order?.openedAt ||
                                      order?.updatedAt ||
                                      order?.createdAt ||
                                      "",
                                  )}
                              </div>
                            </div>
                            <div className="truncate text-slate-600 dark:text-slate-300">
                              {masterToken}
                            </div>
                            <div className="flex items-start">
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                {statusLabel}
                              </span>
                            </div>
                            <div className="text-right font-semibold text-slate-900 dark:text-slate-100">
                              {formatCashRefundMoney(
                                summaryTotal,
                                order?.currencyCode,
                              )}
                            </div>
                            <div className="text-right">
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                onClick={() =>
                                  navigate(
                                    `/cash-management/refunds/${order.orderId}${
                                      searchParams.toString()
                                        ? `?${searchParams.toString()}`
                                        : ""
                                    }`,
                                  )
                                }
                                disabled={isRefunding || isLoadingDetails}
                                className="min-w-20"
                              >
                                View
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-5 py-10 text-center">
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {isSearching
                            ? "Loading orders..."
                            : "No orders matched the selected criteria."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ) : null}
          </Card>
        </>
      ) : null}

      {isShowingDetailView && !selectedOrder ? (
        <Card className="p-6 text-center text-sm text-slate-500 dark:text-slate-400">
          {isSearching || isLoadingDetails
            ? "Loading order details..."
            : "Order details are unavailable."}
        </Card>
      ) : null}

      {showInlineDetails && selectedOrder ? (
        <Card className="overflow-hidden !p-4">
          <div className="grid xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0">
              <div>
                <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      <div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Order Number
                        </div>
                        <div className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
                          {masterToken || "-"}
                        </div>
                      </div>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                      {selectedOrder.displayStatus ||
                        selectedOrder.status ||
                        "Unknown"}
                    </span>
                  </div>
                </div>

                <div className="px-5 py-4">
                  <h3 className="text-base font-medium text-slate-900 dark:text-slate-100">
                    Order Items
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {lines.length} item{lines.length === 1 ? "" : "s"} in this
                    order
                  </p>

                  <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                    <div className="hidden grid-cols-[minmax(0,1fr)_60px_88px_96px] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400 md:grid">
                      <span>Item</span>
                      <span className="text-right">Qty</span>
                      <span className="text-right">Unit Price</span>
                      <span className="text-right">Line Total</span>
                    </div>

                    <div className="max-h-[420px] overflow-y-auto">
                      {lines.length > 0 ? (
                        lines.map((line) => {
                          const modifiers = Array.isArray(line.modifiers)
                            ? line.modifiers
                            : Array.isArray(line.addOns)
                              ? line.addOns
                              : Array.isArray(line.addons)
                                ? line.addons
                                : [];
                          const modifiersText = modifiers
                            .map((modifier) => {
                              const name =
                                modifier.name ||
                                modifier.itemName ||
                                modifier.addOnName ||
                                "Add-on";
                              const price = Number(
                                modifier.priceDelta ?? modifier.price ?? 0,
                              );
                              const quantity = Number(
                                modifier.qty ?? modifier.quantity ?? 1,
                              );
                              return `${name}${price > 0 ? ` +${formatCashRefundMoney(price, paymentState.currencyCode)}` : ""}${quantity > 1 ? ` x${quantity}` : ""}`;
                            })
                            .join(", ");

                          return (
                            <div
                              key={
                                line.orderLineId ||
                                `${line.itemName}-${line.stationCode}`
                              }
                              className="border-b border-slate-100 px-3 py-1.5 last:border-b-0 dark:border-slate-800"
                            >
                              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_60px_88px_96px] md:items-start">
                                <div className="min-w-0">
                                  <span className="text-xs text-slate-900 dark:text-slate-100">
                                    {line.itemName || "Item"}
                                  </span>
                                  {modifiersText ? (
                                    <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 md:pr-4">
                                      <span className="font-medium">
                                        Add-ons:
                                      </span>{" "}
                                      {modifiersText}
                                    </p>
                                  ) : null}
                                  {line.notes ? (
                                    <p className="mt-1 text-[11px] italic text-amber-700 dark:text-amber-300">
                                      Note: {line.notes}
                                    </p>
                                  ) : null}
                                </div>

                                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 md:block md:rounded-none md:bg-transparent md:px-0 md:py-0">
                                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 md:hidden">
                                    Qty
                                  </span>
                                  <span className="text-xs text-slate-700 dark:text-slate-300 md:block md:text-right">
                                    {Number(line.qty || 0)}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 md:block md:rounded-none md:bg-transparent md:px-0 md:py-0">
                                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 md:hidden">
                                    Unit Price
                                  </span>
                                  <span className="text-xs text-slate-700 dark:text-slate-300 md:block md:text-right">
                                    {formatCashRefundMoney(
                                      line.unitPrice,
                                      paymentState.currencyCode,
                                    )}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between rounded-lg bg-slate-900 px-3 py-1.5 text-white md:block md:rounded-none md:bg-transparent md:px-0 md:py-0 md:text-right">
                                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-300 md:hidden">
                                    Line Total
                                  </span>
                                  <span className="text-xs font-normal md:text-slate-900 md:dark:text-slate-100">
                                    {formatCashRefundMoney(
                                      line.lineTotal,
                                      paymentState.currencyCode,
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="py-10 text-center">
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No items available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Payment History
                    </h3>
                  </div>
                  <div className="mt-3 overflow-x-auto border-y border-slate-200 dark:border-slate-700">
                    {payments.length ? (
                      <div className="min-w-[520px]">
                        <div className="grid grid-cols-[minmax(0,1fr)_150px_110px] gap-3 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                          <span>Payment</span>
                          <span>Status</span>
                          <span className="text-right">Amount</span>
                        </div>
                        {payments.map((payment) => (
                          <div
                            key={payment.paymentId}
                            className="grid grid-cols-[minmax(0,1fr)_150px_110px] items-center gap-3 border-t border-slate-100 px-3 py-2.5 text-sm dark:border-slate-800"
                          >
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                                {payment.paymentMethod || "-"}
                              </div>
                              <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                {formatApiDateTime(
                                  payment.capturedAt || payment.createdAt,
                                )}
                              </div>
                            </div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                              {payment.paymentStatus || "-"}
                            </div>
                            <div className="text-right font-semibold text-slate-900 dark:text-slate-100">
                              {formatCashRefundMoney(
                                payment.amount,
                                payment.currencyCode,
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-4 text-sm text-amber-700 dark:text-amber-300">
                        No payment records were found for this order.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <aside className="border-t border-slate-200 bg-slate-50/50 p-5 dark:border-slate-700 dark:bg-slate-900/20 xl:border-l xl:border-t-0">
              <div>
                {paymentState.hasRefund &&
                String(selectedOrder?.orderId || "") !== justRefundedOrderId ? (
                  <div className="mb-4">
                    <Alert tone="warning">
                      A refund has already been recorded for this order.
                    </Alert>
                  </div>
                ) : null}

                <div className="border-b border-slate-200 pb-4 dark:border-slate-700">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    Refund Summary
                  </h2>
                </div>

                <div className="mt-4 space-y-3 text-xs">
                  <div className="flex items-center justify-between text-slate-600 dark:text-slate-300">
                    <span>Order total</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {formatCashRefundMoney(
                        grandTotal,
                        paymentState.currencyCode,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600 dark:text-slate-300">
                    <span>Payment method</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {refundPaymentMethod === "CARD" ? "Card" : "Cash"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-slate-600 dark:text-slate-300">
                    <span>Refund amount</span>
                    <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      {formatCashRefundMoney(
                        paymentState.refundableAmount,
                        paymentState.currencyCode,
                      )}
                    </span>
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  <Textarea
                    label="Refund Reason"
                    value={reasonCode}
                    onChange={(event) => {
                      setReasonCode(event.target.value);
                      setFieldError("");
                      setErrorMessage("");
                    }}
                    error={selectedOrder ? fieldError : ""}
                    placeholder="Enter the refund reason"
                    autoComplete="off"
                    maxLength={500}
                    rows={4}
                    required
                    disabled={!paymentState.isEligible || isRefunding}
                  />

                  {!paymentState.isEligible && !paymentState.hasRefund ? (
                    <Alert tone="warning">
                      {paymentState.ineligibleReason}
                    </Alert>
                  ) : null}

                  <Button
                    type="button"
                    variant="danger"
                    size="lg"
                    className="w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                    onClick={handleOpenConfirmation}
                    disabled={!canOpenConfirmation}
                  >
                    Refund{" "}
                    {formatCashRefundMoney(
                      paymentState.refundableAmount,
                      paymentState.currencyCode,
                    )}
                  </Button>
                </div>
              </div>
            </aside>
          </div>
        </Card>
      ) : null}

      <Modal
        isOpen={isConfirmOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmRefund}
        title="Confirm Refund"
        confirmText="Confirm Refund"
        confirmVariant="danger"
        isLoading={isRefunding}
        size="md"
        align="center"
        showCloseButton={!isRefunding}
        footerStart={
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {formatCashRefundMoney(
              paymentState.refundableAmount,
              paymentState.currencyCode,
            )}
          </span>
        }
      >
        <div className="space-y-4">
          {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-800 dark:bg-rose-900/20">
            <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
              This action processes a {refundPaymentMethod.toLowerCase()} refund
              and cannot be repeated for this order.
            </p>
          </div>
          <dl className="divide-y divide-slate-200 rounded-xl border border-slate-200 px-4 dark:divide-slate-700 dark:border-slate-700">
            {[
              ["Order number", orderNo],
              [
                "Payment method",
                refundPaymentMethod === "CARD" ? "Card" : "Cash",
              ],
              [
                "Refund amount",
                formatCashRefundMoney(
                  paymentState.refundableAmount,
                  paymentState.currencyCode,
                ),
              ],
              ["Reason", finalReason],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-start justify-between gap-4 py-3 text-sm"
              >
                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                <dd className="text-right font-semibold text-slate-900 dark:text-slate-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Confirm only after verifying the order, payment method, and refund
            amount with the customer.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
        onConfirm={() => setIsSuccessOpen(false)}
        title="Refund Processed"
        confirmText="Done"
        showCancel={false}
        confirmVariant="primary"
        size="md"
        align="center"
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-900/20">
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              {message || "Refund processed successfully."}
            </p>
          </div>
          <dl className="divide-y divide-slate-200 rounded-xl border border-slate-200 px-4 dark:divide-slate-700 dark:border-slate-700">
            {[
              ["Order number", orderNo],
              [
                "Payment method",
                refundPaymentMethod === "CARD" ? "Card" : "Cash",
              ],
              [
                "Refund amount",
                formatCashRefundMoney(
                  paymentState.refundableAmount,
                  paymentState.currencyCode,
                ),
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-start justify-between gap-4 py-3 text-sm"
              >
                <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                <dd className="text-right font-semibold text-slate-900 dark:text-slate-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Modal>
    </div>
  );
};

export default CashRefund;
